import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';

const RELEASE_KEY = 'push_launch_v1';

function BellIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 9.5a6 6 0 0 0-12 0c0 6-2.5 7-2.5 7h17S18 15.5 18 9.5Z" />
      <path d="M10.2 20a2.1 2.1 0 0 0 3.6 0" />
      <path d="M18.8 4.2 20 3m-14.8 1.2L4 3" opacity=".65" />
    </svg>
  );
}

export default function PushLaunchModal() {
  const user = useAppStore((s) => s.user);
  const enablePush = useAppStore((s) => s.enablePushNotifications);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!user?.id || user.needs_onboarding) return;
    const key = `${RELEASE_KEY}:${user.id}`;
    if (localStorage.getItem(key)) return;
    // Фиксируем сам факт показа: после закрытия или обновления страницы баннер
    // больше не должен отвлекать этого пользователя на этом устройстве.
    localStorage.setItem(key, new Date().toISOString());
    const timer = window.setTimeout(() => setOpen(true), 650);
    return () => window.clearTimeout(timer);
  }, [user?.id, user?.needs_onboarding]);

  if (!open) return null;

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    const state = await enablePush();
    setBusy(false);
    if (state === 'granted') {
      setResult('ok');
      window.setTimeout(() => setOpen(false), 950);
    } else if (state !== 'default') {
      setResult('error');
    }
  };

  return (
    <div className="push-launch-layer" role="dialog" aria-modal="true" aria-labelledby="push-launch-title">
      <style>{`
        @keyframes pushLaunchBackdrop { from { opacity: 0 } to { opacity: 1 } }
        @keyframes pushLaunchUp { from { opacity: 0; transform: translate3d(0,42px,0) scale(.975) } to { opacity: 1; transform: none } }
        .push-launch-layer { position:fixed; inset:0; z-index:10050; display:flex; align-items:flex-end; justify-content:center; padding:20px; background:rgba(4,4,6,.68); backdrop-filter:blur(8px); animation:pushLaunchBackdrop .28s ease both }
        .push-launch-card { width:min(620px,100%); position:relative; overflow:hidden; padding:30px; border:1px solid rgba(255,55,62,.38); border-radius:24px; color:#fff; background:linear-gradient(145deg,rgba(42,17,20,.98),rgba(13,12,15,.99) 68%); box-shadow:0 28px 90px rgba(0,0,0,.62),0 0 55px rgba(225,18,23,.13); animation:pushLaunchUp .52s cubic-bezier(.16,1,.3,1) both }
        .push-launch-card:before { content:''; position:absolute; width:260px; height:260px; right:-90px; top:-130px; border-radius:50%; background:radial-gradient(circle,rgba(255,45,52,.3),transparent 68%); pointer-events:none }
        .push-launch-top { display:flex; gap:18px; align-items:flex-start }
        .push-launch-mark { width:58px; height:58px; flex:0 0 58px; display:grid; place-items:center; border-radius:18px; color:#fff; background:linear-gradient(145deg,#ff3038,#b80910); box-shadow:0 12px 32px rgba(225,18,23,.34),inset 0 1px rgba(255,255,255,.25) }
        .push-launch-actions { display:flex; gap:10px; margin-top:24px }
        .push-launch-actions button { min-height:50px; border-radius:13px; padding:0 22px; border:1px solid rgba(255,255,255,.13); font:700 13px/1 var(--font-display,sans-serif); letter-spacing:.045em; text-transform:uppercase; cursor:pointer }
        .push-launch-primary { flex:1; color:#fff; background:linear-gradient(120deg,#ff3038,#d20e15); box-shadow:0 12px 32px rgba(225,18,23,.24) }
        .push-launch-secondary { color:rgba(255,255,255,.68); background:rgba(255,255,255,.045) }
        @media (min-width:700px) { .push-launch-layer { align-items:center } }
        @media (max-width:699px) { .push-launch-layer { padding:10px; align-items:flex-end } .push-launch-card { padding:23px 20px calc(20px + env(safe-area-inset-bottom)); border-radius:22px 22px 16px 16px } .push-launch-top { gap:14px } .push-launch-mark { width:50px; height:50px; flex-basis:50px; border-radius:15px } .push-launch-actions { flex-direction:column } .push-launch-actions button { width:100% } }
        @media (prefers-reduced-motion:reduce) { .push-launch-layer,.push-launch-card { animation:none } }
      `}</style>
      <section className="push-launch-card">
        <div className="push-launch-top">
          <div className="push-launch-mark"><BellIcon /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#ff7378', font: '700 10px/1.2 var(--font-mono,monospace)', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 9 }}>
              {t('pushLaunch.eyebrow')}
            </div>
            <h2 id="push-launch-title" style={{ margin: 0, font: '800 clamp(23px,5vw,34px)/1.04 var(--font-display,sans-serif)', textTransform: 'uppercase' }}>
              {result === 'ok' ? t('pushLaunch.readyTitle') : t('pushLaunch.title')}
            </h2>
            <p style={{ margin: '12px 0 0', maxWidth: 470, color: 'rgba(255,255,255,.67)', fontSize: 14, lineHeight: 1.6 }}>
              {result === 'ok' ? t('pushLaunch.readyText') : result === 'error' ? t('pushLaunch.errorText') : t('pushLaunch.text')}
            </p>
          </div>
        </div>
        {result !== 'ok' && (
          <div className="push-launch-actions">
            <button className="push-launch-primary" onClick={activate} disabled={busy}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}><BellIcon size={18} />{busy ? t('pushLaunch.enabling') : t('pushLaunch.enable')}</span>
            </button>
            <button className="push-launch-secondary" onClick={() => setOpen(false)}>{t('pushLaunch.later')}</button>
          </div>
        )}
      </section>
    </div>
  );
}
